import {
  isValidHexColor,
  parseHexColor,
  toHexColor,
  type Rgba,
} from "../core/index.js";
import { MIN_DIMENSION, MAX_DIMENSION } from "../core/index.js";

export const STANDARD_PALETTE: readonly string[] = [
  "#FFB454",
  "#FF6B6B",
  "#8BE28B",
  "#66D9EF",
  "#A78BFA",
  "#F6E58D",
  "#F4F1E8",
  "#8F9CAF",
  "#42526B",
  "#162033",
  "#111827",
  "#000000",
];

export interface SidebarCallbacks {
  readonly onColorChange: (color: Rgba) => void;
  readonly onResizeCanvas: (width: number, height: number) => void;
}

export class Sidebar {
  private readonly element: HTMLElement;
  private readonly callbacks: SidebarCallbacks;

  private colorPreview!: HTMLElement;
  private colorPickerInput!: HTMLInputElement;
  private hexInput!: HTMLInputElement;
  private hexError!: HTMLElement;
  private paletteButtons: HTMLButtonElement[] = [];

  private widthInput!: HTMLInputElement;
  private heightInput!: HTMLInputElement;
  private dimensionError!: HTMLElement;

  constructor(container: HTMLElement, callbacks: SidebarCallbacks) {
    this.element = container;
    this.callbacks = callbacks;
    this.render();
  }

  private render(): void {
    this.element.innerHTML = "";
    this.element.className = "sidebar";

    // Section 1: Color & Palette
    const colorSection = document.createElement("section");
    colorSection.className = "sidebar-card";
    colorSection.innerHTML = `<h2 class="card-title">Farbe</h2>`;

    const colorRow = document.createElement("div");
    colorRow.className = "color-row";

    this.colorPreview = document.createElement("div");
    this.colorPreview.className = "color-preview";
    this.colorPreview.title = "Aktive Farbe";

    this.colorPickerInput = document.createElement("input");
    this.colorPickerInput.type = "color";
    this.colorPickerInput.className = "color-picker-input";
    this.colorPickerInput.title = "Farbe wählen";
    this.colorPickerInput.value = "#ffb454";
    this.colorPickerInput.addEventListener("input", () => {
      const hex = this.colorPickerInput.value;
      if (isValidHexColor(hex)) {
        const parsed = parseHexColor(hex);
        this.callbacks.onColorChange(parsed);
      }
    });

    this.colorPreview.appendChild(this.colorPickerInput);

    const hexContainer = document.createElement("div");
    hexContainer.className = "hex-container";

    this.hexInput = document.createElement("input");
    this.hexInput.type = "text";
    this.hexInput.className = "hex-input";
    this.hexInput.maxLength = 7;
    this.hexInput.placeholder = "#RRGGBB";
    this.hexInput.value = "#FFB454";
    this.hexInput.setAttribute("aria-label", "Hexadezimaler Farbwert");

    this.hexInput.addEventListener("input", () => {
      let val = this.hexInput.value.trim();
      if (!val.startsWith("#") && val.length > 0) {
        val = "#" + val;
      }
      if (isValidHexColor(val)) {
        this.hexError.textContent = "";
        this.hexError.style.display = "none";
        this.hexInput.classList.remove("has-error");
        const parsed = parseHexColor(val);
        this.callbacks.onColorChange(parsed);
      } else {
        this.hexError.textContent = "Format: #RRGGBB";
        this.hexError.style.display = "block";
        this.hexInput.classList.add("has-error");
      }
    });

    this.hexError = document.createElement("div");
    this.hexError.className = "field-error";
    this.hexError.style.display = "none";

    hexContainer.appendChild(this.hexInput);
    hexContainer.appendChild(this.hexError);

    colorRow.appendChild(this.colorPreview);
    colorRow.appendChild(hexContainer);
    colorSection.appendChild(colorRow);

    // Palette Grid
    const paletteTitle = document.createElement("h3");
    paletteTitle.className = "card-subtitle";
    paletteTitle.textContent = "Standardpalette";
    colorSection.appendChild(paletteTitle);

    const paletteGrid = document.createElement("div");
    paletteGrid.className = "palette-grid";

    this.paletteButtons = [];
    for (const hex of STANDARD_PALETTE) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "palette-swatch";
      btn.style.backgroundColor = hex;
      btn.title = hex;
      btn.setAttribute("aria-label", `Farbe ${hex}`);
      btn.dataset.hex = hex;
      btn.addEventListener("click", () => {
        const parsed = parseHexColor(hex);
        this.callbacks.onColorChange(parsed);
      });
      paletteGrid.appendChild(btn);
      this.paletteButtons.push(btn);
    }
    colorSection.appendChild(paletteGrid);
    this.element.appendChild(colorSection);

    // Section 2: Canvas Dimensions
    const dimSection = document.createElement("section");
    dimSection.className = "sidebar-card";
    dimSection.innerHTML = `<h2 class="card-title">Leinwandgröße</h2>`;

    const dimRow = document.createElement("div");
    dimRow.className = "dimension-row";

    const widthGroup = document.createElement("div");
    widthGroup.className = "dimension-group";
    widthGroup.innerHTML = `<label for="dim-width" class="field-label">Breite</label>`;
    this.widthInput = document.createElement("input");
    this.widthInput.id = "dim-width";
    this.widthInput.type = "number";
    this.widthInput.className = "dimension-input";
    this.widthInput.min = String(MIN_DIMENSION);
    this.widthInput.max = String(MAX_DIMENSION);
    this.widthInput.value = "16";
    widthGroup.appendChild(this.widthInput);

    const timesLabel = document.createElement("span");
    timesLabel.className = "dimension-times";
    timesLabel.textContent = "×";

    const heightGroup = document.createElement("div");
    heightGroup.className = "dimension-group";
    heightGroup.innerHTML = `<label for="dim-height" class="field-label">Höhe</label>`;
    this.heightInput = document.createElement("input");
    this.heightInput.id = "dim-height";
    this.heightInput.type = "number";
    this.heightInput.className = "dimension-input";
    this.heightInput.min = String(MIN_DIMENSION);
    this.heightInput.max = String(MAX_DIMENSION);
    this.heightInput.value = "16";
    heightGroup.appendChild(this.heightInput);

    dimRow.appendChild(widthGroup);
    dimRow.appendChild(timesLabel);
    dimRow.appendChild(heightGroup);
    dimSection.appendChild(dimRow);

    this.dimensionError = document.createElement("div");
    this.dimensionError.className = "field-error";
    this.dimensionError.style.display = "none";
    dimSection.appendChild(this.dimensionError);

    const applyDimBtn = document.createElement("button");
    applyDimBtn.type = "button";
    applyDimBtn.className = "button button-secondary button-block";
    applyDimBtn.textContent = "Größe anwenden";
    applyDimBtn.addEventListener("click", () => {
      const w = Number.parseInt(this.widthInput.value, 10);
      const h = Number.parseInt(this.heightInput.value, 10);
      if (
        !Number.isInteger(w) ||
        !Number.isInteger(h) ||
        w < MIN_DIMENSION ||
        w > MAX_DIMENSION ||
        h < MIN_DIMENSION ||
        h > MAX_DIMENSION
      ) {
        this.dimensionError.textContent = `Werte müssen zwischen ${MIN_DIMENSION} und ${MAX_DIMENSION} liegen.`;
        this.dimensionError.style.display = "block";
        return;
      }
      this.dimensionError.textContent = "";
      this.dimensionError.style.display = "none";
      this.callbacks.onResizeCanvas(w, h);
    });
    dimSection.appendChild(applyDimBtn);
    this.element.appendChild(dimSection);

    // Section 3: Quick Help / Shortcuts
    const helpSection = document.createElement("section");
    helpSection.className = "sidebar-card help-card";
    helpSection.innerHTML = `
      <h2 class="card-title">Schnellhilfe</h2>
      <ul class="help-list">
        <li><kbd>LMB</kbd> <span>Malen (aktive Farbe)</span></li>
        <li><kbd>RMB</kbd> <span>Löschen (transparent)</span></li>
        <li><kbd>Mausrad</kbd> <span>Stufenloser Zoom</span></li>
        <li><kbd>Leertaste</kbd> + Ziehen <span>Leinwand verschieben</span></li>
        <li><kbd>P</kbd> <span>Stift</span> &middot; <kbd>E</kbd> <span>Radierer</span> &middot; <kbd>I</kbd> <span>Pipette</span></li>
        <li><kbd>Strg+Z</kbd> / <kbd>Strg+Y</kbd> <span>Rückgängig / Wiederholen</span></li>
      </ul>
    `;
    this.element.appendChild(helpSection);
  }

  public setColor(color: Rgba): void {
    const hex = toHexColor(color);
    this.colorPreview.style.backgroundColor = hex;
    this.colorPickerInput.value = hex.toLowerCase();
    if (this.hexInput !== document.activeElement) {
      this.hexInput.value = hex;
      this.hexError.style.display = "none";
      this.hexInput.classList.remove("has-error");
    }

    for (const btn of this.paletteButtons) {
      const btnHex = btn.dataset.hex;
      const isSelected = btnHex?.toUpperCase() === hex.toUpperCase();
      btn.classList.toggle("is-active", isSelected);
      btn.setAttribute("aria-pressed", isSelected ? "true" : "false");
    }
  }

  public setDimensions(width: number, height: number): void {
    this.widthInput.value = String(width);
    this.heightInput.value = String(height);
    this.dimensionError.style.display = "none";
  }
}
